import Styled from 'styled-components/macro';
import {Avatar, tokensForThemeProvider} from '@forecast-it/design-system';

/**
 * The log's timeline chrome. There is no design-system primitive for a vertical rail — Separator
 * is horizontal and takes no props — so the line is drawn here, using the shared tokens rather
 * than literal colours.
 *
 * Spacing between entries lives INSIDE each entry (as TimelineBody's bottom padding) rather than
 * as a column gap, so the stretched rail also spans the space between entries instead of breaking
 * at each gap — stacked entries read as one continuous line leading down to the divider.
 */
export const Timeline = Styled.div`
	display: flex;
	flex-direction: column;
`;

export const TimelineEntry = Styled.div`
	display: flex;
	flex-direction: row;
	align-items: flex-start;
	gap: ${tokensForThemeProvider.sky.sys.spacing[300]};
`;

/**
 * The marker's column: the rail passes THROUGH the circle — a short fixed segment above it, and
 * below it the line runs the entry's full remaining height.
 */
export const TimelineRail = Styled.div`
	display: flex;
	flex-direction: column;
	align-items: center;
	flex-shrink: 0;
	align-self: stretch;

	&::before {
		content: '';
		width: 1px;
		height: 8px;
		background-color: ${tokensForThemeProvider.sky.sys.color.border.decorative};
	}

	&::after {
		content: '';
		width: 1px;
		flex: 1;
		background-color: ${tokensForThemeProvider.sky.sys.color.border.decorative};
	}
`;

/** The rail's marker: the entry's event icon in a primary-tinted circle. */
export const TimelineMarker = Styled.div`
	display: flex;
	align-items: center;
	justify-content: center;
	width: 28px;
	height: 28px;
	border-radius: 50%;
	background-color: ${tokensForThemeProvider.sky.sys.color.background.primary['1']};
	flex-shrink: 0;
`;

export const TimelineBody = Styled.div`
	display: flex;
	flex-direction: column;
	gap: ${tokensForThemeProvider.sky.sys.spacing[100]};
	flex: 1;
	min-width: 0;
	padding-bottom: ${tokensForThemeProvider.sky.sys.spacing[500]};
`;

/**
 * The entry's timestamp, in the design system's tabular mono style (the tags font token) so the
 * digits line up in one column. No typography component exposes that token, hence the styled span.
 */
export const Timestamp = Styled.span`
	${tokensForThemeProvider.sky.sys.font.tags['2']};
	color: ${tokensForThemeProvider.sky.sys.color.text.content.auxiliary};
	white-space: nowrap;
`;

/** The design-system Avatar is a rounded square; the log shows people as circles. */
export const RoundAvatar = Styled(Avatar)`
	border-radius: 50%;
`;

/** The email correspondence nested under a sent entry, indented off the entry's own left edge. */
export const ReplyThread = Styled.div`
	display: flex;
	flex-direction: column;
	gap: ${tokensForThemeProvider.sky.sys.spacing[300]};
	padding-top: ${tokensForThemeProvider.sky.sys.spacing[300]};
	padding-left: ${tokensForThemeProvider.sky.sys.spacing[400]};
`;

/**
 * One correspondence entry. The curved connector off the left edge is drawn here — a quarter-round
 * corner from the parent's line down to the entry's header — because no design-system primitive
 * draws connectors.
 */
export const ReplyEntry = Styled.div`
	position: relative;
	display: flex;
	flex-direction: column;
	gap: ${tokensForThemeProvider.sky.sys.spacing[100]};
	padding-left: ${tokensForThemeProvider.sky.sys.spacing[400]};

	&::before {
		content: '';
		position: absolute;
		left: 0;
		top: -4px;
		width: 12px;
		height: 14px;
		border-left: 1px solid ${tokensForThemeProvider.sky.sys.color.border.decorative};
		border-bottom: 1px solid ${tokensForThemeProvider.sky.sys.color.border.decorative};
		border-bottom-left-radius: 8px;
	}
`;

/** A client's message: filled, so their words read as received content. */
export const ReplyBody = Styled.div`
	background-color: ${tokensForThemeProvider.sky.sys.color.background.neutral['2']};
	border-radius: 8px;
	padding: ${tokensForThemeProvider.sky.sys.spacing[300]};
`;

/** The user's own sent message: outlined on the surface, so it reads as authored here. */
export const ReplyBodyOutbound = Styled.div`
	background-color: ${tokensForThemeProvider.sky.sys.color.background.neutral['1']};
	border: 1px solid ${tokensForThemeProvider.sky.sys.color.border.decorative};
	border-radius: 8px;
	padding: ${tokensForThemeProvider.sky.sys.spacing[300]};
`;

/** Ends the timeline and separates the log from the decision footer. */
export const DashedDivider = Styled.div`
	border-top: 1px dashed ${tokensForThemeProvider.sky.sys.color.border.decorative};
`;
